import { calculateChartRange } from '@/components/charts/generic';
import { CHARTJS_MAX_TICKS_LIMIT } from '@/components/charts/generic/constants';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { WelfareData } from '@/domain/welfare/types';
import {
    HEALTH_LINE_TENSION,
    HEALTH_POINT_MARKER,
    healthPercentAxis,
    healthTooltipTitle,
    percentPoints,
} from '@/routes/Client/Cage/pages/Health/_utils/healthChartSpec';
import { formatNumberByLocale, type Locale, parseLocale } from '@/utils/localeFormatters';

const MATURATION_COLOR = graphSettings.colors.primary;

/** Match legacy OptoBarChart suggestedMaxMin({ min: 0, max: 5 }) for small fully-mature %. */
const WEEKLY_MATURATION_SUGGESTED_MAX_PERCENT = 5;
/** The daily axis reaches at least this far, so a few mature fish do not fill the plot. */
const DAILY_MATURATION_MIN_MAX_PERCENT = 5;

/** A unique x key per ISO week: the same week number recurs every year. */
function weekKey(week: { isoYear: number; isoWeek: number }): string {
    return `${week.isoYear}-W${String(week.isoWeek).padStart(2, '0')}`;
}

/**
 * Weekly share of fish with a mature jaw (Bioscope 1.0), in percent, as a MetricChart bar spec
 * with the Chart.js legend above it. The axis reads `week 31`; ticks are whole percentages and
 * the tooltip shows two decimals.
 */
export function buildMaturationWeeklySpec(
    welfareData: WelfareData,
    matureLabel: string,
    localeInput: string,
    weekLabel = 'week'
): MetricChartSpec<'bar'> {
    const locale = parseLocale(localeInput);
    const weekly = [...welfareData.weekly].sort((a, b) => weekKey(a).localeCompare(weekKey(b)));
    const labelByKey = new Map(
        weekly.map((week) => [weekKey(week), `${weekLabel} ${week.isoWeek}`])
    );
    const color = graphSettings.colors.primary;

    return {
        kind: 'bar',
        xAxisKind: 'category',
        days: weekly.map(weekKey),
        categoryLabel: (key) => labelByKey.get(key) ?? key,
        series: [
            {
                key: 'matureJaw',
                label: matureLabel,
                color,
                barBorder: { color, width: 2 },
                // A week without a matureJaw estimate is a gap, not a measured 0%.
                data: weekly.map((week) => ({
                    day: weekKey(week),
                    value: week.matureJaw == null ? null : Number(week.matureJaw) * 100,
                })),
            },
        ],
        left: {
            label: '',
            min: 0,
            suggestedMax: WEEKLY_MATURATION_SUGGESTED_MAX_PERCENT,
            stepSize: 1,
            // One tick per percent up to 10 %, rather than every other one past 5 %.
            maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT,
            format: (value, target) =>
                `${formatNumberByLocale(value, locale, target === 'tick' ? 0 : 2)}%`,
        },
        locale,
        legend: 'canvas',
        zoom: false,
    };
}

/**
 * Daily share of fish with a mature jaw (Bioscope 1.0), in percent. The axis starts at 0 and
 * reaches at least 5 %, so a handful of mature fish does not fill the plot.
 */
export function buildMaturationDailySpec(
    welfareData: WelfareData,
    labels: { matureJaw: string; axis: string },
    locale: Locale
): MetricChartSpec<'line'> {
    const { days } = welfareData;
    return {
        kind: 'line',
        days: days.map((day) => day.day),
        series: [
            {
                key: 'matureJaw',
                label: labels.matureJaw,
                color: MATURATION_COLOR,
                tension: HEALTH_LINE_TENSION,
                marker: HEALTH_POINT_MARKER,
                data: percentPoints(days, (day) => day.matureJaw),
            },
        ],
        left: {
            ...healthPercentAxis(labels.axis),
            fitRange: (values) => {
                const { min, max } = calculateChartRange(values, 5, true);
                return {
                    min: Math.min(0, min),
                    max: Math.max(DAILY_MATURATION_MIN_MAX_PERCENT, max),
                };
            },
        },
        locale,
        tooltipTitle: healthTooltipTitle(locale),
        tooltipSkipEmpty: true,
    };
}
